import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import DOMPurify from "dompurify";
import {
  ArrowLeft, ThumbsUp, ThumbsDown, CheckCircle, XCircle,
  ExternalLink, Sparkles, Loader2, RefreshCw, User, ArrowLeftRight,
  Clock, MinusCircle, Download, Trophy, Activity, BarChart2, Bell, Mail,
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { FollowPoliticianButton } from "../components/FollowPoliticianButton";
import { CampaignPromisesSection } from "../components/politician/CampaignPromisesSection";
import { VotingRecordSection } from "../components/politician/VotingRecordSection";
import { TVFYSection } from "../components/politician/TVFYSection";
import { AIAnalysisSection } from "../components/politician/AIAnalysisSection";
import { AlertSubscribeSection } from "../components/politician/AlertSubscribeSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Climate: "#10B981",
  Housing: "#3B82F6",
  Economy: "#F59E0B",
  Indigenous: "#DC2626",
  Disability: "#8B5CF6",
  Environment: "#059669",
  Integrity: "#6366F1",
  Defence: "#1E40AF",
  Education: "#0EA5E9",
  "Social Policy": "#EC4899",
  "Gambling Reform": "#D97706",
  "Foreign Policy": "#64748B",
  Energy: "#EF4444",
  Immigration: "#7C3AED",
  Agriculture: "#065F46",
  "Digital Safety": "#0891B2",
  "Human Rights": "#BE185D",
  default: "#6B7280",
};

function renderAnalysis(text) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    if (line.startsWith("**") && line.endsWith("**")) {
      const heading = line.replace(/\*\*/g, "");
      return (
        <h3 key={i} className="text-base font-black mt-5 mb-1.5" style={{ color: "#FFC82E", fontFamily: "Outfit, sans-serif" }}>
          {heading}
        </h3>
      );
    }
    if (line.startsWith("**")) {
      return (
        <p key={i} className="font-black text-sm mb-1" dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")),
        }} />
      );
    }
    if (!line.trim()) return <div key={i} className="h-2" />;
    return (
      <p key={i} className="text-sm leading-relaxed mb-1">
        {line}
      </p>
    );
  });
}

export default function PoliticianProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [politician, setPolitician] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiCached, setAiCached] = useState(false);
  const [error, setError] = useState("");
  const [cardGenerating, setCardGenerating] = useState(false);
  const reportCardRef = useRef(null);
  const [tvfyData, setTvfyData] = useState(null);
  const [tvfyLoading, setTvfyLoading] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertStatus, setAlertStatus] = useState("");
  const [alertSubscribing, setAlertSubscribing] = useState(false);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    setLoading(true);
    setError("");
    axios
      .get(`${API}/politicians/${id}`)
      .then((r) => setPolitician(r.data))
      .catch(() => setError("Politician not found."))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setTvfyLoading(true);
    axios
      .get(`${API}/tvfy/politician/${id}/scores`)
      .then((r) => { if (r.data && r.data.tvfy_id) setTvfyData(r.data); })
      .catch(() => {})
      .finally(() => setTvfyLoading(false));
  }, [id]);

  const generateAnalysis = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/politicians/${id}/ai-analysis`);
      setAiAnalysis(r.data.analysis);
      setAiCached(r.data.cached);
    } catch {
      setAiAnalysis("Unable to generate analysis. Please try again.");
    } finally {
      setAiLoading(false);
    }
  };

  const subscribeAlert = async () => {
    if (!alertEmail || !alertEmail.includes("@")) {
      setAlertStatus("Please enter a valid email address.");
      return;
    }
    setAlertSubscribing(true);
    setAlertStatus("");
    try {
      await axios.post(`${API}/notifications/politician-alert`, {
        politician_id: id,
        politician_name: politician?.name,
        email: alertEmail,
        alert_types: ["new_vote", "promise_update"],
      });
      setAlertStatus("subscribed");
      setAlertEmail("");
    } catch {
      setAlertStatus("Failed to subscribe. Please try again.");
    } finally {
      setAlertSubscribing(false);
    }
  };

  const downloadReportCard = async () => {
    if (!reportCardRef.current) return;
    setCardGenerating(true);
    try {
      const canvas = await html2canvas(reportCardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#0F172A",
        logging: false,
      });
      const link = document.createElement("a");
      link.download = `${politician.name.replace(/ /g, "-")}-report-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error("Report card generation failed:", e);
    } finally {
      setCardGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#FFC82E" }} />
      </div>
    );
  }

  if (error || !politician) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ backgroundColor: bg }}>
        <p className="text-lg font-black" style={{ color: text }}>{error || "Politician not found"}</p>
        <button
          onClick={() => navigate("/politicians")}
          className="px-4 py-2 border-2 border-black font-bold text-sm"
          style={{ backgroundColor: "#FFC82E", color: "#000" }}
        >
          Back to Politicians
        </button>
      </div>
    );
  }

  const ayeVotes = politician.key_votes?.filter((v) => v.vote === "aye") || [];
  const noVotes = politician.key_votes?.filter((v) => v.vote === "no") || [];
  const totalVotes = politician.key_votes?.length || 0;
  const ayePct = totalVotes > 0 ? Math.round((ayeVotes.length / totalVotes) * 100) : 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="politician-profile-page">
      {/* Hero */}
      <div className="border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        {/* Party color banner */}
        <div className="h-2" style={{ backgroundColor: politician.party_color }} />
        <div className="max-w-5xl mx-auto px-4 py-8 relative">
          <button
            onClick={() => navigate("/politicians")}
            className="flex items-center gap-1.5 text-sm font-bold mb-4 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
            data-testid="back-politicians-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All Politicians
          </button>

          {/* Action buttons: inline row on mobile, absolute at top-right on sm+ */}
          <div className="flex items-center gap-2 mb-6 sm:absolute sm:top-8 sm:right-4 sm:mb-0">
            <div className="sm:order-first">
              <FollowPoliticianButton politicianId={id} politicianName={politician?.name} />
            </div>
            <button
              onClick={downloadReportCard}
              disabled={cardGenerating}
              className="flex items-center gap-1.5 text-xs font-black px-3 py-2 border-2 border-white hover:-translate-y-0.5 transition-transform disabled:opacity-60"
              style={{ color: "#fff", backgroundColor: "#2E5948", boxShadow: "2px 2px 0 rgba(255,255,255,0.3)" }}
              data-testid="download-report-card-btn"
            >
              {cardGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Report Card
            </button>
            <button
              onClick={() => navigate(`/politicians/compare?p1=${id}`)}
              className="flex items-center gap-1.5 text-xs font-black px-3 py-2 border-2 border-white hover:-translate-y-0.5 transition-transform"
              style={{ color: "#000", backgroundColor: "#FFC82E", boxShadow: "2px 2px 0 rgba(255,255,255,0.3)" }}
              data-testid="compare-politician-btn"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" /> Compare
            </button>
          </div>          <div className="flex items-start gap-4">
            {/* Avatar placeholder */}
            <div
              className="w-16 h-16 flex-shrink-0 border-2 border-white flex items-center justify-center"
              style={{ backgroundColor: politician.party_color + "30" }}
            >
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1
                className="text-3xl sm:text-4xl font-black text-white mb-1"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="politician-name"
              >
                {politician.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span
                  className="text-xs font-black px-2.5 py-1 border-2 border-black text-white"
                  style={{ backgroundColor: politician.party_color }}
                  data-testid="politician-party"
                >
                  {politician.party_name}
                </span>
                <span
                  className="text-xs font-bold px-2.5 py-1 border border-white text-white"
                >
                  {politician.role}
                </span>
                <span className="text-xs font-bold" style={{ color: "#94A3B8" }}>
                  {politician.chamber} · {politician.state}
                  {politician.electorate ? ` · ${politician.electorate}` : ""}
                </span>
              </div>
              <p className="text-xs" style={{ color: "#64748B" }}>
                Elected {politician.elected_year}
                {politician.website && (
                  <>
                    {" · "}
                    <a
                      href={politician.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline inline-flex items-center gap-0.5"
                      style={{ color: "#FFC82E" }}
                    >
                      Official website <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Vote stats */}
          {totalVotes > 0 && (
            <div className="flex flex-wrap gap-3 mt-6">
              {[
                { label: "Votes on Record", value: totalVotes, color: "#FFC82E" },
                { label: "Aye Votes", value: ayeVotes.length, color: "#10B981" },
                { label: "No Votes", value: noVotes.length, color: "#EF4444" },
                { label: "Aye Rate", value: `${ayePct}%`, color: "#38BDF8" },
              ].map(({ label, value, color }) => (
                <div
                  key={label}
                  className="border-2 border-black px-4 py-2"
                  style={{ backgroundColor: color + "15" }}
                  data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <span className="text-xl font-black" style={{ color }}>{value}</span>
                  <span className="text-xs font-bold ml-2 text-white">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Bio */}
        <section data-testid="bio-section">
          <h2
            className="text-xl font-black mb-3 pb-2 border-b-2 border-black"
            style={{ color: text, fontFamily: "Outfit, sans-serif" }}
          >
            Biography
          </h2>
          <p className="text-sm leading-relaxed" style={{ color: textSec }}>
            {politician.bio}
          </p>
        </section>

        {/* Campaign Promises */}
        <CampaignPromisesSection
          promises={politician.campaign_promises}
          isDark={isDark}
          surface={surface}
          text={text}
          textSec={textSec}
        />

        {/* Voting Record */}
        <VotingRecordSection
          keyVotes={politician.key_votes || []}
          totalVotes={totalVotes}
          surface={surface}
          text={text}
          textSec={textSec}
        />

        {/* Live Parliamentary Data (TVFY) */}
        <TVFYSection
          tvfyData={tvfyData}
          tvfyLoading={tvfyLoading}
          isDark={isDark}
          surface={surface}
          text={text}
          textSec={textSec}
        />

        {/* AI Analysis */}
        <AIAnalysisSection
          aiAnalysis={aiAnalysis}
          aiLoading={aiLoading}
          aiCached={aiCached}
          generateAnalysis={generateAnalysis}
          politician={politician}
          surface={surface}
          text={text}
          textSec={textSec}
        />

      {/* Hidden Report Card for html2canvas capture */}
      <div
        ref={reportCardRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: "600px",
          backgroundColor: "#0F172A",
          fontFamily: "Outfit, sans-serif",
          padding: "0",
          overflow: "hidden",
        }}
        aria-hidden="true"
      >
        {politician && (() => {
          const promises = politician.campaign_promises || [];
          const delivered = promises.filter((p) => p.status === "delivered").length;
          const inProg = promises.filter((p) => p.status === "in_progress").length;
          const broken = promises.filter((p) => p.status === "broken").length;
          const total = promises.length;
          const pct = total ? Math.round((delivered / total) * 100) : 0;
          const ayeCount = (politician.key_votes || []).filter((v) => v.vote === "aye").length;
          const noCount = (politician.key_votes || []).filter((v) => v.vote === "no").length;
          return (
            <div>
              {/* Top bar */}
              <div style={{ height: "8px", backgroundColor: politician.party_color }} />
              {/* Header */}
              <div style={{ padding: "24px 28px 20px", borderBottom: "2px solid #334155" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: "900", letterSpacing: "2px", color: "#FFC82E", marginBottom: "6px", textTransform: "uppercase" }}>
                      AusVote · Politician Report Card
                    </div>
                    <div style={{ fontSize: "32px", fontWeight: "900", color: "#FFFFFF", lineHeight: "1.1", marginBottom: "6px" }}>
                      {politician.name}
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ backgroundColor: politician.party_color, color: "#fff", fontSize: "11px", fontWeight: "900", padding: "3px 8px", border: "2px solid #000" }}>
                        {politician.party_name}
                      </span>
                      <span style={{ color: "#94A3B8", fontSize: "12px", fontWeight: "700" }}>
                        {politician.role}
                      </span>
                    </div>
                    <div style={{ color: "#64748B", fontSize: "11px", marginTop: "4px" }}>
                      {politician.chamber} · {politician.state}
                      {politician.electorate ? ` · ${politician.electorate}` : ""}
                      {" · "}Elected {politician.elected_year}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "52px", fontWeight: "900", color: pct >= 60 ? "#10B981" : pct >= 30 ? "#F59E0B" : "#EF4444", lineHeight: "1" }}>
                      {pct}%
                    </div>
                    <div style={{ fontSize: "11px", color: "#94A3B8", fontWeight: "700", textTransform: "uppercase" }}>Delivery rate</div>
                  </div>
                </div>
              </div>

              {/* Promise stats */}
              <div style={{ padding: "20px 28px", borderBottom: "2px solid #334155" }}>
                <div style={{ fontSize: "11px", fontWeight: "900", color: "#FFC82E", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>
                  Campaign Promises
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  {[
                    { label: "Delivered", value: delivered, color: "#10B981" },
                    { label: "In Progress", value: inProg, color: "#F59E0B" },
                    { label: "Broken", value: broken, color: "#EF4444" },
                    { label: "Total", value: total, color: "#94A3B8" },
                  ].map((s) => (
                    <div key={s.label} style={{ flex: 1, backgroundColor: "#1E293B", border: "2px solid #334155", padding: "12px", textAlign: "center" }}>
                      <div style={{ fontSize: "24px", fontWeight: "900", color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: "10px", color: "#94A3B8", fontWeight: "700" }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {/* Bar */}
                {total > 0 && (
                  <div style={{ display: "flex", height: "8px", marginTop: "12px", border: "1px solid #334155", overflow: "hidden" }}>
                    {delivered > 0 && <div style={{ width: `${(delivered / total) * 100}%`, backgroundColor: "#10B981" }} />}
                    {inProg > 0 && <div style={{ width: `${(inProg / total) * 100}%`, backgroundColor: "#F59E0B" }} />}
                    {broken > 0 && <div style={{ width: `${(broken / total) * 100}%`, backgroundColor: "#EF4444" }} />}
                  </div>
                )}
              </div>

              {/* Voting record */}
              {(politician.key_votes || []).length > 0 && (
                <div style={{ padding: "20px 28px", borderBottom: "2px solid #334155" }}>
                  <div style={{ fontSize: "11px", fontWeight: "900", color: "#FFC82E", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>
                    Voting Record
                  </div>
                  <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
                    <div style={{ backgroundColor: "#10B981" + "20", border: "2px solid #334155", padding: "10px 16px", flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: "20px", fontWeight: "900", color: "#10B981" }}>{ayeCount}</div>
                      <div style={{ fontSize: "10px", color: "#94A3B8" }}>AYE votes</div>
                    </div>
                    <div style={{ backgroundColor: "#EF4444" + "20", border: "2px solid #334155", padding: "10px 16px", flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: "20px", fontWeight: "900", color: "#EF4444" }}>{noCount}</div>
                      <div style={{ fontSize: "10px", color: "#94A3B8" }}>NO votes</div>
                    </div>
                    <div style={{ backgroundColor: "#FFC82E" + "20", border: "2px solid #334155", padding: "10px 16px", flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: "20px", fontWeight: "900", color: "#FFC82E" }}>{ayeCount + noCount}</div>
                      <div style={{ fontSize: "10px", color: "#94A3B8" }}>Total</div>
                    </div>
                  </div>
                  {/* Top 3 recent votes */}
                  {(politician.key_votes || []).slice(0, 3).map((vote) => (
                    <div key={vote.id} style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "6px" }}>
                      <span style={{ backgroundColor: vote.vote === "aye" ? "#10B981" : "#EF4444", color: "#fff", fontSize: "9px", fontWeight: "900", padding: "2px 5px", flexShrink: 0, marginTop: "2px" }}>
                        {vote.vote.toUpperCase()}
                      </span>
                      <span style={{ fontSize: "11px", color: "#CBD5E1", flex: 1 }}>{vote.bill} ({vote.date.slice(0, 4)})</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div style={{ padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ width: "24px", height: "24px", backgroundColor: "#2E5948", display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid #000" }}>
                    <span style={{ color: "#FFC82E", fontSize: "12px", fontWeight: "900" }}>A</span>
                  </div>
                  <span style={{ color: "#94A3B8", fontSize: "11px", fontWeight: "900" }}>AusVote · ausvote.app</span>
                </div>
                <span style={{ color: "#475569", fontSize: "10px" }}>
                  {new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long" })}
                </span>
              </div>
            </div>
          );
        })()}
      </div>
      </div>

      <AlertSubscribeSection
        politician={politician}
        alertEmail={alertEmail}
        setAlertEmail={setAlertEmail}
        alertStatus={alertStatus}
        alertSubscribing={alertSubscribing}
        subscribeAlert={subscribeAlert}
        isDark={isDark}
      />
    </div>
  );
}
